import { BadRequestException, Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { tmpdir } from 'node:os';
import { join } from 'node:path';
import { PrismaClient } from '@prisma/client';
import { PrismaService } from '@cart/api-data-access';
import { DbAgentClient } from './db-agent.client';
import { BackupQuotaService } from './backup-quota.service';
import { BackupAlertService } from './backup-alert.service';
import { LocalBackupService } from './local-backup.service';
import { BackupReportService } from './backup-report.service';
import { MerchantSnapshotService } from './merchant-snapshot.service';
import {
  backupBucket,
  backupEnvPrefix,
  isProductionBackupEnv,
  walFileName,
  walPrefix,
} from './backup-storage.constants';
import { databaseUrl } from './database-url';

/**
 * Turns backup policies into backup runs.
 *
 * ── SCHEDULES LIVE IN THE TABLE, NOT IN DECORATORS ───────────────────────────
 *
 * There is exactly one @Cron here, a minute tick that asks "what is due?".
 * Every actual schedule is a `cronExpression` on a BackupPolicy row, which is
 * the whole point of the admin screen: changing when the weekly full runs is an
 * edit, not a deploy. Putting the schedules in decorators would make the UI
 * decorative — a display of settings it cannot actually change.
 *
 * ── EVERY RUN IS ADMITTED AGAINST THE BUDGET FIRST ───────────────────────────
 *
 * This deployment has a hard 1 GB R2 ceiling. pgBackRest expires by count and
 * age and has no notion of a byte budget, so nothing in it would stop the
 * repository growing until writes start failing — at which point archive-push
 * fails, WAL backs up, and the system reports healthy backups while having
 * none. Admission happens before the run starts, and a refusal is recorded as
 * a row rather than a log line, so "why did last night's backup not happen?"
 * is answerable from the same screen as everything else.
 */
/**
 * Backup types the local backend can perform.
 *
 * `snapshot` belongs here because a merchant snapshot IS a logical export.
 * `diff`/`incr` do not, and never will: pg_dump has no differential form, and
 * pg_basebackup produces only full physical copies. Those need pgBackRest.
 *
 * Exported so the API can tell the screen which policies are runnable HERE,
 * rather than offering a button that always refuses.
 */
export interface PitrPlan {
  feasible: boolean;
  reason: string | null;
  targetTime: string;
  /** Which R2 bucket the objects live in. */
  bucket: string | null;
  /**
   * What the archive covers, and what it does not.
   *
   * `unprotectedMinutes` is the honest data-loss window: changes newer than the
   * last archived segment exist only on the server.
   */
  coverage?: {
    firstSegment: { name: string; archivedAt: string | null; path: string } | null;
    lastSegment: { name: string; archivedAt: string | null; path: string } | null;
    unprotectedSince: string;
    unprotectedMinutes: number;
  } | null;
  baseBackup: { prefix: string; takenAt: string; replaySpanMinutes: number } | null;
  walSegments: Array<{ name: string; archivedAt: string | null; bytes: number }>;
  walCount: number;
  walBytes: number;
  /** Things that will still let the restore run, but change what it produces. */
  warnings: string[];
}

/**
 * How far past "now" a restore target may sit before it is refused.
 *
 * Not zero: the browser supplies the timestamp and its clock is not the
 * server's, so a small skew must not block a legitimate restore.
 */
export const FUTURE_TARGET_TOLERANCE_MINUTES = 3;

export const LOCAL_BACKUP_TYPES = ['logical', 'basebackup', 'snapshot'];

@Injectable()
export class BackupService {
  private readonly logger = new Logger(BackupService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly agent: DbAgentClient,
    private readonly quota: BackupQuotaService,
    private readonly alerts: BackupAlertService,
    private readonly local: LocalBackupService,
    private readonly snapshots: MerchantSnapshotService,
    private readonly reports: BackupReportService
  ) {}

  /**
   * Which backend runs a backup on this host.
   *
   * The agent (pgBackRest, physical, real PITR) when it is present; otherwise
   * the local pg_dump path, so the whole subsystem — schedules, quota, run
   * history, retention, the admin screen — is exercisable on a developer
   * machine. `none` only when neither exists, and then a run is refused with an
   * explanation rather than recorded as a failure.
   */
  private backend(): 'agent' | 'local' | 'none' {
    if (this.agent.isConfigured()) return 'agent';
    if (this.local.isAvailable()) return 'local';
    return 'none';
  }

  /** The only schedule in this file. Everything else is data. */
  @Cron('* * * * *')
  async tick(): Promise<void> {
    if (this.backend() === 'none') return;

    await this.initialiseSchedules();

    const due = await this.prisma.backupPolicy.findMany({
      where: { isEnabled: true, isActive: true, deletedAt: null, nextRunAt: { lte: new Date() } },
      include: { target: true },
    });

    for (const policy of due) {
      await this.runPolicy(policy.id, 'scheduled').catch((err) =>
        this.logger.error(`policy ${policy.name} failed: ${err.message}`)
      );
    }
  }

  /**
   * Give any enabled policy without a next run one.
   *
   * The due query is `nextRunAt <= now`, and NULL never matches it — so a
   * policy seeded by a migration (which has no way to compute a fire time in
   * SQL) sits in the UI showing "enabled" and never runs. Silently. That is
   * precisely the class of failure this subsystem exists to eliminate, and it
   * was real: both the base-backup and dev-dump policies were in that state,
   * while the four older ones only had a time because an earlier bug let manual
   * runs write one.
   *
   * Self-healing here rather than fixed once in a migration, so the next person
   * to seed a policy cannot reintroduce it.
   */
  private async initialiseSchedules(): Promise<void> {
    const unscheduled = await this.prisma.backupPolicy.findMany({
      where: { isEnabled: true, isActive: true, deletedAt: null, nextRunAt: null },
      select: { id: true, name: true, cronExpression: true },
    });

    for (const policy of unscheduled) {
      const next = this.nextRun(policy.cronExpression);
      if (!next) {
        this.logger.warn(`policy "${policy.name}" has an invalid cron expression and cannot be scheduled`);
        continue;
      }
      await this.prisma.backupPolicy.update({ where: { id: policy.id }, data: { nextRunAt: next } });
      this.logger.log(`scheduled "${policy.name}" for ${next.toISOString()}`);
    }
  }

  /**
   * Can this host run this backup type, and if not, why?
   *
   * The screen used to offer "Run now" on every policy regardless. On a machine
   * without pgBackRest that meant four of six buttons existed only to produce a
   * refusal — which reads as "backups are broken" rather than "this host cannot
   * do differentials". A button that can only fail should not be a button.
   */
  supportFor(backupType: string): { supported: boolean; reason: string | null } {
    const backend = this.backend();

    if (backend === 'none') {
      return {
        supported: false,
        reason: 'No backup backend on this host — no pgBackRest agent, and pg_dump was not found.',
      };
    }
    if (backend === 'agent') return { supported: true, reason: null };

    if (LOCAL_BACKUP_TYPES.includes(backupType)) return { supported: true, reason: null };

    return {
      supported: false,
      reason:
        `${backupType} backups need pgBackRest, which does not run on this host. ` +
        `pg_dump has no differential or incremental form.`,
    };
  }

  async runPolicy(policyId: string, trigger: string, actorId?: string): Promise<{ runId: string | null; message: string }> {
    const policy = await this.prisma.backupPolicy.findUnique({
      where: { id: policyId },
      include: { target: true },
    });
    if (!policy) throw new BadRequestException('Policy not found');

    // Checked here as well as in tick(), because "Run now" reaches this method
    // directly. Refusing is only correct when there is genuinely nowhere to
    // write: on a machine with pg_dump we fall through to the local backend
    // rather than recording a failure and raising a CRITICAL alert, which
    // would be both wrong and the fastest way to teach people that critical
    // alerts from this subsystem can be ignored.
    const backend = this.backend();
    if (backend === 'none') {
      return {
        runId: null,
        message:
          'No backup backend on this host: no agent (DBAGENT_SECRET unset) and pg_dump was not ' +
          'found. Set PG_DUMP_PATH to run local dumps.',
      };
    }

    // pg_dump has no notion of differential or incremental: EVERY logical dump
    // is a complete copy. Silently running a `diff` policy as a full dump —
    // which is what an earlier version did — produces a run history where
    // "Daily differential", "6-hourly incremental" and "Weekly full" are all
    // the same 95 MB file, and an operator reasonably concludes differentials
    // are working when no such thing exists on this host. Refuse instead, and
    // say which policy to use.
    //
    // Checked BEFORE the schedule is touched: a refusal must leave the policy
    // exactly as it found it.
    // `basebackup` is the physical type this host CAN do — pg_basebackup ships
    // with Postgres and works on Windows, unlike pgBackRest. It is the one that
    // makes point-in-time recovery possible here, because WAL replays onto a
    // physical copy and has nothing to replay onto a logical dump.
    if (backend === 'local' && !LOCAL_BACKUP_TYPES.includes(policy.backupType)) {
      // A refusal deliberately does not advance nextRunAt, so this policy stays
      // permanently due and is refused again on the next tick — once a minute,
      // forever, returning a message nobody reads because tick() discards it.
      // On the deployed server three policies sat in exactly that state while
      // the screen showed them "enabled", which is the same silent-failure
      // shape as the NULL nextRunAt bug. Raise it so it is visible; the dedupe
      // keeps it to one row however often it repeats.
      await this.alerts.raise({
        kind: 'policy_unsupported',
        severity: 'critical',
        targetId: policy.targetId,
        title: `"${policy.name}" cannot run on this host`,
        detail: {
          policyId: policy.id,
          backupType: policy.backupType,
          backend,
          supported: LOCAL_BACKUP_TYPES,
        },
      });

      return {
        runId: null,
        message:
          `"${policy.name}" is a ${policy.backupType} backup, which needs pgBackRest. ` +
          `pg_dump has no differential or incremental form, so this host can only take ` +
          `full logical dumps or physical base backups — use the "Local pg_dump → R2 (dev)" ` +
          `or "Base backup → R2 (PITR)" policy instead.`,
      };
    }

    // Advance the schedule BEFORE running, but only for a scheduled run.
    //
    // Two separate reasons, and conflating them was a bug:
    //
    //   - Scheduled: advance first, because a backup that outlives the tick
    //     interval would otherwise be picked up again and run concurrently
    //     with itself.
    //   - Manual: do NOT advance. "Run now" is an extra backup on top of the
    //     schedule, not a replacement for the next one. The earlier version
    //     moved nextRunAt on every manual click, so pressing "Run now" at
    //     01:59 on a Sunday silently cancelled the 02:00 weekly backup — the
    //     button quietly disabled the thing it appeared to be exercising.
    if (trigger === 'scheduled') {
      await this.prisma.backupPolicy.update({
        where: { id: policy.id },
        data: { nextRunAt: this.nextRun(policy.cronExpression), lastRunAt: new Date() },
      });
    }

    if (policy.skipIfRunning) {
      const running = await this.prisma.backupRun.findFirst({
        where: { policyId: policy.id, status: 'running' },
      });
      if (running) {
        return { runId: null, message: 'previous run still in progress — skipped' };
      }
    }

    if (trigger === 'scheduled' && !this.insideWindow(policy.cronExpression, policy.windowMinutes)) {
      // A full backup that slipped into business hours should skip rather than
      // compete with merchant traffic on a 2-vCPU box.
      return { runId: null, message: 'outside the configured window — skipped' };
    }

    // Both backends write to the same object store — a dev dump lands under
    // `test/` and a server backup under `production/` — so both are charged
    // against the same S3 repository and the same 1 GB ceiling. That is the
    // honest accounting: they occupy the same bytes in the same bucket.
    const repository = await this.prisma.backupRepository.findFirst({
      where: { kind: 's3', isActive: true },
      orderBy: { repoIndex: 'asc' },
    });
    if (!repository) throw new BadRequestException('No active S3 repository configured');

    const verdict = await this.quota.admit(repository.id, policy.backupType);
    if (!verdict.admitted) {
      const refused = await this.prisma.backupRun.create({
        data: {
          targetId: policy.targetId,
          policyId: policy.id,
          repositoryId: repository.id,
          backupType: policy.backupType,
          trigger,
          status: 'refused_quota',
          error: verdict.reason,
          finishedAt: new Date(),
          actorId,
        },
      });
      this.logger.warn(`${policy.name}: ${verdict.reason}`);
      return { runId: refused.id, message: verdict.reason };
    }

    const run = await this.prisma.backupRun.create({
      data: {
        targetId: policy.targetId,
        policyId: policy.id,
        repositoryId: repository.id,
        backupType: policy.backupType,
        trigger,
        status: 'running',
        // Stamped now, from the ledger head, so a restore of this backup knows
        // which migrations to roll forward. A backup that does not know its own
        // schema version restores data the running code cannot read.
        schemaVersion: await this.schemaHead(),
        actorId,
      },
    });

    const started = Date.now();

    try {
      if (policy.backupType === 'snapshot') {
        // Delegated, so the policy table stays the single source of schedule —
        // the whole premise of the admin screen.
        const result = await this.snapshots.runNightly();

        await this.prisma.backupRun.update({
          where: { id: run.id },
          data: {
            status: 'completed',
            label: `${result.created} merchant snapshot(s)`,
            manifestKey: result.storageKey,
            fileCount: result.created,
            sizeBytes: BigInt(result.bytes),
            repoSizeBytes: BigInt(result.bytes),
            finishedAt: new Date(),
            durationMs: Date.now() - started,
          },
        });
      } else if (backend === 'local' && policy.backupType === 'basebackup') {
        // A PHYSICAL base backup: the starting position WAL replays onto.
        //
        // Compressed server-side, which is what makes it affordable against a
        // 1 GB budget — measured on this database, 435 MB raw becomes 45 MB, and
        // the raw bytes never cross the wire.
        const base = await this.local.runBaseBackup();

        await this.prisma.backupRun.update({
          where: { id: run.id },
          data: {
            status: 'completed',
            label: base.prefix,
            manifestKey: base.prefix,
            // sizeBytes is what the cluster actually is; repoSizeBytes is what
            // the budget pays for. Keeping both makes the compression ratio
            // visible instead of folklore.
            sizeBytes: BigInt(base.rawBytes),
            repoSizeBytes: BigInt(base.sizeBytes),
            checksum: base.checksum,
            fileCount: base.files.length,
            finishedAt: new Date(),
            durationMs: base.durationMs,
          },
        });

        if (policy.retentionCount) await this.local.pruneBaseBackups(policy.retentionCount);
      } else if (backend === 'local') {
        // pg_dump: a LOGICAL backup. Recoverable only to the instant it ran,
        // not to any second in a window. Recorded as backupType 'logical' so
        // nothing downstream — least of all the recovery timeline — mistakes it
        // for a physical base backup with continuous WAL behind it.
        const dump = await this.local.runDump();

        await this.prisma.backupRun.update({
          where: { id: run.id },
          data: {
            backupType: 'logical',
            status: 'completed',
            label: dump.storageKey,
            manifestKey: dump.storageKey,
            sizeBytes: BigInt(dump.sizeBytes),
            repoSizeBytes: BigInt(dump.sizeBytes),
            checksum: dump.checksum,
            fileCount: 1,
            finishedAt: new Date(),
            durationMs: dump.durationMs,
          },
        });

        if (policy.retentionCount) await this.local.prune(policy.retentionCount);
      } else {
        const result = await this.agent.call('backup.run', {
          stanza: policy.target.stanza,
          type: policy.backupType,
          repo: repository.repoIndex,
        });

        if (!result.ok) throw new Error(result.stderr.slice(0, 2000) || `exit ${result.code}`);

        await this.reconcileRun(run.id, policy.target.stanza, repository.repoIndex);

        await this.prisma.backupRun.update({
          where: { id: run.id },
          data: { status: 'completed', finishedAt: new Date(), durationMs: Date.now() - started },
        });
      }

      await this.prisma.backupPolicy.update({
        where: { id: policy.id },
        data: { consecutiveFailures: 0 },
      });

      await this.alerts.resolve('run_failed', policy.targetId);
      if (backend === 'agent') await this.quota.refresh(policy.target.stanza);
      else await this.refreshLocalUsage(repository.id);

      // Fire-and-forget: a report that cannot be sent must not turn a backup
      // that worked into one recorded as failed.
      //
      // The .catch() is not belt-and-braces. sendRunReport swallows its own
      // errors, but a bare `void` on a promise that ever rejects is an
      // unhandled rejection, and Node's default for those is to kill the
      // process — which would turn "the mail server is down" into "the API
      // restarted mid-backup".
      this.reports.sendRunReport(run.id).catch((err) => {
        this.logger.warn(`run report failed for ${run.id}: ${(err as Error).message}`);
      });

      return { runId: run.id, message: 'completed' };
    } catch (err) {
      await this.prisma.backupRun.update({
        where: { id: run.id },
        data: {
          status: 'failed',
          error: (err as Error).message,
          finishedAt: new Date(),
          durationMs: Date.now() - started,
        },
      });

      await this.prisma.backupPolicy.update({
        where: { id: policy.id },
        data: { consecutiveFailures: { increment: 1 } },
      });

      // First failure, not a threshold. A backup that failed once is already a
      // problem: the next one is 24 hours away, and the window it should have
      // covered is now uncovered whatever happens next.
      await this.alerts.raise({
        kind: 'run_failed',
        severity: 'critical',
        targetId: policy.targetId,
        title: `Backup "${policy.name}" failed`,
        detail: { error: (err as Error).message, backupType: policy.backupType },
      });

      // Awaited, unlike the success path: this is the mail carrying the full
      // error text, and it is worth a second to make sure it left.
      await this.reports.sendRunReport(run.id);

      throw err;
    }
  }

  /**
   * Copy pgBackRest's own numbers onto our row.
   *
   * The database is the manifest of record; this reads back the LSN range,
   * sizes and label so the row is complete even though pgBackRest also knows
   * them. A manifest that lives only inside the thing it describes is not a
   * manifest — the same rule the media backup subsystem already follows.
   */
  private async reconcileRun(runId: string, stanza: string, repoIndex: number): Promise<void> {
    const info = await this.agent.backupInfo(stanza, repoIndex);
    const backups = info.flatMap((s) => s.backup ?? []);
    if (!backups.length) return;

    const latest = backups[backups.length - 1];

    await this.prisma.backupRun.update({
      where: { id: runId },
      data: {
        label: latest.label,
        lsnStart: latest.lsn?.start ?? null,
        lsnStop: latest.lsn?.stop ?? null,
        walStart: latest.archive?.start ?? null,
        walStop: latest.archive?.stop ?? null,
        sizeBytes: BigInt(latest.info?.size ?? 0),
        repoSizeBytes: BigInt(latest.info?.repository?.size ?? 0),
      },
    });
  }

  /** Base backups available as PITR starting positions, newest first. */
  async listBaseBackups(): Promise<Array<{ prefix: string; timestamp: string; takenAt: string }>> {
    const backups = await this.local.listBaseBackups();
    return backups.map((b) => ({
      ...b,
      // 20260829_211500 -> ISO, so the UI can place it on the timeline.
      takenAt: this.parseStamp(b.timestamp).toISOString(),
    }));
  }

  private parseStamp(stamp: string): Date {
    const m = /^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})$/.exec(stamp);
    if (!m) return new Date(0);
    return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
  }

  /**
   * What a restore to `targetTime` would actually use — without doing it.
   *
   * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
   *
   * "Restore to 15:29" is a request; "restore the 22:18 base backup and replay
   * 41 WAL segments up to 15:29" is a plan someone can check. The difference
   * matters because the two most common PITR failures are silent until it is
   * too late: picking a target the archive cannot reach, and a missing segment
   * partway through the replay. Both are visible here, before anything runs.
   *
   * `restoreToPointInTime` calls this and uses its answer, so what is previewed
   * is exactly what executes — a preview computed differently from the action
   * it describes is worse than no preview.
   */
  async planPointInTime(targetTime: Date): Promise<PitrPlan> {
    const warnings: string[] = [];

    // A target in the future cannot be recovered to — the WAL for it does not
    // exist yet. Postgres would not error: it would replay everything it has
    // and stop at the end, silently producing "now" instead of the requested
    // instant. Refusing is the only way that difference is visible.
    //
    // A few minutes of tolerance because the browser's clock and the server's
    // are not the same clock, and a 30-second skew should not block a restore.
    const skewMs = FUTURE_TARGET_TOLERANCE_MINUTES * 60 * 1000;
    if (targetTime.getTime() > Date.now() + skewMs) {
      return {
        feasible: false,
        reason:
          `That instant is in the future. WAL for it does not exist yet, so recovery would stop ` +
          `at the newest change it has rather than the moment requested. Choose a time at or ` +
          `before now.`,
        targetTime: targetTime.toISOString(),
        bucket: backupBucket(),
        baseBackup: null,
        walSegments: [],
        walCount: 0,
        walBytes: 0,
        warnings,
      };
    }

    const available = await this.local.listBaseBackups();

    if (!available.length) {
      return {
        feasible: false,
        reason:
          'No base backup exists. WAL replays onto a physical copy of the data files, and a ' +
          'logical dump cannot serve as one — run the "Base backup → R2 (PITR)" policy first.',
        targetTime: targetTime.toISOString(),
        bucket: backupBucket(),
        baseBackup: null,
        walSegments: [],
        walCount: 0,
        walBytes: 0,
        warnings,
      };
    }

    // Only a base backup taken at or BEFORE the target can reach it: replay
    // moves forward only.
    const usable = available
      .map((b) => ({ ...b, takenAt: this.parseStamp(b.timestamp) }))
      .filter((b) => b.takenAt <= targetTime)
      .sort((a, b) => b.takenAt.getTime() - a.takenAt.getTime());

    if (!usable.length) {
      const oldest = available
        .map((b) => this.parseStamp(b.timestamp))
        .sort((a, b) => a.getTime() - b.getTime())[0];
      return {
        feasible: false,
        reason:
          `The oldest base backup was taken at ${oldest.toISOString()}, which is AFTER the ` +
          `requested instant. WAL only replays forward, so nothing can reach that moment.`,
        targetTime: targetTime.toISOString(),
        bucket: backupBucket(),
        baseBackup: null,
        walSegments: [],
        walCount: 0,
        walBytes: 0,
        warnings,
      };
    }

    const chosen = usable[0];

    // Everything archived between the base backup and the target is replayed.
    const segments = await this.prisma.walArchiveSegment.findMany({
      where: {
        status: 'uploaded',
        archivedAt: { gte: chosen.takenAt, lte: new Date(targetTime.getTime() + 60 * 60 * 1000) },
      },
      orderBy: [{ timeline: 'asc' }, { segmentName: 'asc' }],
      select: {
        segmentName: true,
        timeline: true,
        archivedAt: true,
        compressedBytes: true,
      },
    });

    // A hole here stops replay dead — recovery applies segments in strict order
    // and cannot skip one. Finding it now beats finding it mid-restore.
    for (let i = 1; i < segments.length; i++) {
      if (!this.isConsecutive(segments[i - 1].segmentName, segments[i].segmentName)) {
        warnings.push(
          `Gap between ${segments[i - 1].segmentName} and ${segments[i].segmentName} — ` +
            `replay stops at the gap, so anything after it is unreachable.`
        );
      }
    }

    const newest = segments[segments.length - 1];
    if (newest?.archivedAt && newest.archivedAt < targetTime) {
      const minutes = Math.round((targetTime.getTime() - newest.archivedAt.getTime()) / 60000);
      warnings.push(
        `The newest archived WAL is ${minutes} min before the target. Recovery will stop at the ` +
          `last change it has, not at the instant requested — the remainder is still in the ` +
          `open segment on the server.`
      );
    }

    if (!segments.length) {
      warnings.push(
        'No WAL covers this range, so the restore lands at the base backup instant rather than ' +
          'the target.'
      );
    }

    const first = segments[0];
    const last = segments[segments.length - 1];

    // What is NOT protected: everything since the newest archived segment lives
    // only in the open WAL file on the server, on the same disk as the data it
    // is meant to protect. If that disk were lost right now, so would those
    // changes be. This is the real RPO, and it is invisible unless stated.
    const unprotectedSince = last?.archivedAt ?? chosen.takenAt;
    const unprotectedMinutes = Math.max(
      0,
      Math.round((Date.now() - unprotectedSince.getTime()) / 60000)
    );

    return {
      feasible: true,
      reason: null,
      targetTime: targetTime.toISOString(),
      bucket: backupBucket(),
      coverage: {
        firstSegment: first
          ? {
              name: first.segmentName,
              archivedAt: first.archivedAt?.toISOString() ?? null,
              path: `${walPrefix()}/${walFileName(first.segmentName)}`,
            }
          : null,
        lastSegment: last
          ? {
              name: last.segmentName,
              archivedAt: last.archivedAt?.toISOString() ?? null,
              path: `${walPrefix()}/${walFileName(last.segmentName)}`,
            }
          : null,
        unprotectedSince: unprotectedSince.toISOString(),
        unprotectedMinutes,
      },
      baseBackup: {
        prefix: chosen.prefix,
        takenAt: chosen.takenAt.toISOString(),
        // How far the replay has to travel — the honest proxy for how long this
        // will take and how much can go wrong.
        replaySpanMinutes: Math.round((targetTime.getTime() - chosen.takenAt.getTime()) / 60000),
      },
      walSegments: segments.slice(0, 200).map((s) => ({
        name: s.segmentName,
        archivedAt: s.archivedAt?.toISOString() ?? null,
        bytes: s.compressedBytes,
      })),
      walCount: segments.length,
      walBytes: segments.reduce((sum, s) => sum + s.compressedBytes, 0),
      warnings,
    };
  }

  /**
   * Are these two segment names adjacent?
   *
   * Segment counter runs 00..FE within a logical file (Postgres skips FF), then
   * the logical file increments and the counter restarts. Same rule as
   * RecoveryWindowService — too strict invents phantom gaps, too loose misses
   * real ones.
   */
  private isConsecutive(a: string, b: string): boolean {
    if (a.length !== 24 || b.length !== 24 || a.slice(0, 8) !== b.slice(0, 8)) return false;
    const logA = parseInt(a.slice(8, 16), 16);
    const segA = parseInt(a.slice(16, 24), 16);
    const logB = parseInt(b.slice(8, 16), 16);
    const segB = parseInt(b.slice(16, 24), 16);
    if (logB === logA && segB === segA + 1) return true;
    return logB === logA + 1 && segA === 0xfe && segB === 0x00;
  }

  /**
   * Point-in-time recovery: restore a base backup and replay WAL to an instant.
   *
   * ── ALWAYS INTO A SANDBOX CLUSTER, NEVER OVER THE LIVE ONE ──────────────────
   *
   * A logical restore may target the live database, because it replaces objects
   * inside it. A PHYSICAL restore replaces the entire data directory — every
   * database in the cluster at once, with the server stopped. That is not
   * something to drive from a web request even in development: it takes the
   * platform down, and the swap belongs in the runbook where maintenance mode
   * and the rollback path are written out.
   *
   * So this starts a SECOND Postgres on its own port and promotion stays manual.
   * The logical path relaxes this rule outside production; the physical path
   * does not, because the blast radius is the whole cluster rather than one
   * database.
   */
  async restoreToPointInTime(
    input: { targetTime: Date; basePrefix?: string; reason: string },
    actorId: string
  ): Promise<{ jobId: string; port: number; dataDir: string; message: string }> {
    const target = await this.prisma.backupTarget.findFirst({ where: { isActive: true } });
    if (!target) throw new BadRequestException('No active backup target');

    // The same plan the screen previewed. Computing the selection twice, in two
    // places, is how a preview and its action drift apart.
    const plan = await this.planPointInTime(input.targetTime);
    if (!plan.feasible) throw new BadRequestException(plan.reason ?? 'Not recoverable to that instant.');

    const chosen = input.basePrefix ?? (plan.baseBackup as { prefix: string }).prefix;

    const port = await this.allocateSandboxPort();
    const dataDir = join(process.env.PITR_SANDBOX_ROOT ?? tmpdir(), `forgestack-pitr-${port}`);

    const job = await this.prisma.restoreJob.create({
      data: {
        targetId: target.id,
        scope: 'cluster',
        environment: 'sandbox',
        status: 'restoring',
        phase: 'fetching base backup and WAL',
        targetTime: input.targetTime,
        sandboxPort: port,
        sandboxName: `pitr_${port}`,
        reason: input.reason,
        requestedBy: actorId,
        sourceRunId: null,
        startedAt: new Date(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000),
      },
    });

    try {
      await this.local.restoreToPointInTime({
        basePrefix: chosen,
        targetTime: input.targetTime,
        dataDir,
        port,
      });

      await this.prisma.restoreJob.update({
        where: { id: job.id },
        data: { phase: 'replaying WAL', progressPct: 60 },
      });

      await this.local.startSandbox(dataDir, port);

      const checks = await this.verifySandboxCluster(port);
      const failed = checks.filter((c) => c.status === 'failed').length;

      for (const check of checks) {
        await this.prisma.restoreVerification.create({
          data: {
            restoreJobId: job.id,
            checkType: check.checkType,
            name: check.name,
            actual: check.actual,
            status: check.status,
          },
        });
      }

      await this.prisma.restoreJob.update({
        where: { id: job.id },
        data: {
          status: failed ? 'failed' : 'ready',
          phase: failed ? `${failed} verification(s) failed` : `recovered, running on port ${port}`,
          progressPct: 100,
          finishedAt: new Date(),
        },
      });

      return {
        jobId: job.id,
        port,
        dataDir,
        message: failed
          ? `Recovered to ${input.targetTime.toISOString()} on port ${port}, but ${failed} check(s) failed.`
          : `Recovered to ${input.targetTime.toISOString()}. Running on port ${port} — inspect it, then tear it down.`,
      };
    } catch (err) {
      await this.prisma.restoreJob.update({
        where: { id: job.id },
        data: { status: 'failed', error: (err as Error).message, finishedAt: new Date() },
      });
      throw new BadRequestException((err as Error).message);
    }
  }

  /** Stop a PITR sandbox and remove its data directory. */
  async teardownPitr(jobId: string): Promise<{ message: string }> {
    const job = await this.prisma.restoreJob.findUnique({ where: { id: jobId } });
    if (!job?.sandboxPort) throw new BadRequestException('Not a sandbox restore');

    const dataDir = join(
      process.env.PITR_SANDBOX_ROOT ?? tmpdir(),
      `forgestack-pitr-${job.sandboxPort}`
    );

    await this.local.stopSandbox(dataDir);

    await this.prisma.restoreJob.update({
      where: { id: jobId },
      data: { status: 'discarded', phase: 'sandbox torn down' },
    });

    return { message: `Sandbox on port ${job.sandboxPort} stopped and removed.` };
  }

  private async allocateSandboxPort(): Promise<number> {
    const active = await this.prisma.restoreJob.findMany({
      where: { status: { in: ['restoring', 'replaying', 'verifying', 'ready'] } },
      select: { sandboxPort: true },
    });
    const taken = new Set(active.map((j) => j.sandboxPort).filter(Boolean));
    // 5432 is production. Never.
    for (let port = 5433; port <= 5499; port++) if (!taken.has(port)) return port;
    throw new BadRequestException('No sandbox port free — tear down an existing restore first.');
  }

  /**
   * Query the recovered cluster to prove it is genuinely usable.
   *
   * A PITR that starts but cannot answer a query has not recovered anything,
   * and the log line saying "database system is ready" is not evidence of that.
   */
  private async verifySandboxCluster(
    port: number
  ): Promise<Array<{ checkType: string; name: string; status: string; actual?: string }>> {
    const url = databaseUrl();
    url.port = String(port);
    const sandbox = new PrismaClient({ datasources: { db: { url: url.toString() } } });

    try {
      const rows = await sandbox.$queryRawUnsafe<Array<{ n: bigint }>>(
        'SELECT count(*)::bigint AS n FROM products.products'
      );
      return [
        { checkType: 'connectivity', name: `Recovered cluster answers on port ${port}`, status: 'passed' },
        {
          checkType: 'row_count',
          name: 'products readable at the recovered instant',
          actual: String(rows[0]?.n ?? 0),
          status: 'passed',
        },
      ];
    } catch (err) {
      return [
        {
          checkType: 'connectivity',
          name: `Recovered cluster answers on port ${port}`,
          status: 'failed',
          actual: (err as Error).message.slice(0, 200),
        },
      ];
    } finally {
      await sandbox.$disconnect().catch(() => undefined);
    }
  }

  /**
   * A one-off backup with no policy behind it.
   *
   * Used for the pre-restore safety copy, which must happen regardless of any
   * schedule, window or retention rule — those exist to pace routine backups,
   * and none of their reasons apply when the next statement is going to
   * overwrite a live database.
   *
   * Quota still applies: if there is genuinely no room, the honest outcome is to
   * refuse the restore rather than take an unprotected one.
   */
  private async runPolicyless(
    trigger: string,
    actorId: string
  ): Promise<{ runId: string | null; message: string }> {
    const target = await this.prisma.backupTarget.findFirst({ where: { isActive: true } });
    const repository = await this.prisma.backupRepository.findFirst({
      where: { kind: 's3', isActive: true },
    });
    if (!target || !repository) return { runId: null, message: 'no target or repository configured' };

    const verdict = await this.quota.admit(repository.id, 'logical');
    if (!verdict.admitted) return { runId: null, message: verdict.reason };

    const run = await this.prisma.backupRun.create({
      data: {
        targetId: target.id,
        repositoryId: repository.id,
        backupType: 'logical',
        trigger,
        status: 'running',
        schemaVersion: await this.schemaHead(),
        actorId,
      },
    });

    try {
      const dump = await this.local.runDump();
      await this.prisma.backupRun.update({
        where: { id: run.id },
        data: {
          status: 'completed',
          label: dump.storageKey,
          manifestKey: dump.storageKey,
          sizeBytes: BigInt(dump.sizeBytes),
          repoSizeBytes: BigInt(dump.sizeBytes),
          checksum: dump.checksum,
          fileCount: 1,
          finishedAt: new Date(),
          durationMs: dump.durationMs,
        },
      });
      await this.refreshLocalUsage(repository.id);
      return { runId: run.id, message: 'completed' };
    } catch (err) {
      await this.prisma.backupRun.update({
        where: { id: run.id },
        data: { status: 'failed', error: (err as Error).message, finishedAt: new Date() },
      });
      return { runId: null, message: (err as Error).message };
    }
  }

  /**
   * Restore a logical backup into a scratch database, and verify it.
   *
   * Recorded as a RestoreJob so a restore has the same audit trail as a backup:
   * who asked, from which object, into what, how long, and — crucially — what
   * the verification found. "The restore said SUCCESS" is not evidence; the
   * RestoreVerification rows are.
   *
   * Always a scratch database, never the live one. The dump carries DROP
   * statements, so restoring over production is a decision for a human at a
   * terminal with the runbook open, not an HTTP request with a flag on it.
   */
  async restoreRun(
    runId: string,
    actorId: string,
    options: { target?: 'scratch' | 'live'; approverId?: string } = {}
  ): Promise<{ jobId: string; database: string; message: string }> {
    const run = await this.prisma.backupRun.findUnique({ where: { id: runId } });
    if (!run) throw new BadRequestException('Backup run not found');

    if (run.backupType !== 'logical' || !run.manifestKey) {
      throw new BadRequestException(
        'Only a completed logical (pg_dump) backup can be restored from here. Physical backups ' +
          'restore through pgBackRest into a sandbox cluster — see the runbook.'
      );
    }
    if (run.status === 'deleted') {
      throw new BadRequestException('This backup was deleted; its object no longer exists.');
    }

    const target = await this.prisma.backupTarget.findFirst({ where: { isActive: true } });
    if (!target) throw new BadRequestException('No active backup target');

    // Where the restore lands, decided by the environment rather than by a
    // hardcoded rule: on a dev machine "restore" means this machine's database;
    // on the server it means the server's. `scratch` stays available for
    // verifying a backup without disturbing anything.
    const live = this.local.liveDatabase();
    const toLive = options.target === 'live';

    if (toLive && isProductionBackupEnv() && !options.approverId) {
      // The one place the environment changes the rules. Overwriting a dev
      // database is a developer's own business; overwriting production is not a
      // decision one person makes from a web page.
      throw new BadRequestException(
        'Restoring over the production database needs a second approver. Use the runbook ' +
          '(docs/26-database-restore-runbook.md §4) — it also puts the site in maintenance mode.'
      );
    }

    // Derived from the dump's own timestamp, so a scratch database names the
    // backup it came from rather than the moment someone pressed the button.
    const stamp = (run.manifestKey.match(/(\d{8}_\d{6})/)?.[1] ?? Date.now().toString()).replace(/\D/g, '');
    const database = toLive ? live : `${live || 'restore'}_r${stamp}`.slice(0, 63);

    // The undo of the undo. Taken BEFORE anything is overwritten, so the state
    // being replaced is itself recoverable — without this, a restore to the
    // wrong backup is unrecoverable, which is the one outcome this subsystem
    // must never produce.
    let preRestoreRunId: string | null = null;
    if (toLive) {
      const safety = await this.runPolicyless('pre_restore', actorId);
      preRestoreRunId = safety.runId;
      if (!safety.runId) {
        throw new BadRequestException(
          `Refusing to overwrite "${database}": the pre-restore safety backup failed (${safety.message}). ` +
            `Nothing has been changed.`
        );
      }
    }

    const job = await this.prisma.restoreJob.create({
      data: {
        targetId: target.id,
        sourceRunId: run.id,
        scope: 'database',
        databaseName: database,
        status: 'restoring',
        phase: 'downloading and applying the dump',
        reason: `Restore of ${run.manifestKey} requested from the admin screen`,
        requestedBy: actorId,
        schemaVersionRestored: run.schemaVersion,
        environment: toLive ? backupEnvPrefix() : 'sandbox',
        preRestoreRunId,
        approvedBy: options.approverId,
        startedAt: new Date(),
        // Scratch restores are reaped after a day — a forgotten one is 95 MB of
        // somebody's disk. A restore INTO the live database has nothing to reap.
        expiresAt: toLive ? null : new Date(Date.now() + 24 * 60 * 60 * 1000),
      },
    });

    try {
      const result = await this.local.restoreDump(run.manifestKey, database, toLive);

      const head = await this.schemaHead();
      const checks = await this.verifyRestoredDatabase(database, run.schemaVersion, head);

      const failed = checks.filter((c) => c.status === 'failed').length;

      for (const check of checks) {
        await this.prisma.restoreVerification.create({
          data: {
            restoreJobId: job.id,
            checkType: check.checkType,
            name: check.name,
            expected: check.expected,
            actual: check.actual,
            status: check.status,
          },
        });
      }

      await this.prisma.restoreJob.update({
        where: { id: job.id },
        data: {
          status: failed ? 'failed' : 'ready',
          phase: failed ? `${failed} verification(s) failed` : 'restored and verified',
          progressPct: 100,
          bytesDownloaded: BigInt(result.sizeBytes),
          schemaVersionTarget: head,
          finishedAt: new Date(),
        },
      });

      return {
        jobId: job.id,
        database,
        message: failed
          ? `Restored into "${database}", but ${failed} verification check(s) failed.`
          : `Restored into "${database}" in ${Math.round(result.durationMs / 1000)}s and verified.`,
      };
    } catch (err) {
      await this.prisma.restoreJob.update({
        where: { id: job.id },
        data: { status: 'failed', error: (err as Error).message, finishedAt: new Date() },
      });
      throw new BadRequestException((err as Error).message);
    }
  }

  /**
   * Confirm the restored database is actually usable.
   *
   * The schema check is the one that matters and the one a naive smoke test
   * misses: a dump older than the running code restores perfectly and then
   * throws on the first query touching a column that did not exist yet, while
   * `SELECT count(*)` passes throughout.
   */
  private async verifyRestoredDatabase(
    database: string,
    restoredVersion: string | null,
    head: string | null
  ): Promise<Array<{ checkType: string; name: string; status: string; expected?: string; actual?: string }>> {
    const checks: Array<{ checkType: string; name: string; status: string; expected?: string; actual?: string }> = [];

    const url = databaseUrl();
    url.pathname = `/${database}`;
    const scratch = new PrismaClient({ datasources: { db: { url: url.toString() } } });

    try {
      const rows = await scratch.$queryRawUnsafe<Array<Record<string, bigint>>>(
        `SELECT (SELECT count(*) FROM products.products)  AS products,
                (SELECT count(*) FROM sales.orders)       AS orders,
                (SELECT count(*) FROM customer.customers) AS customers`
      );

      checks.push({ checkType: 'connectivity', name: `Scratch database "${database}" accepts queries`, status: 'passed' });

      for (const [table, count] of Object.entries(rows[0] ?? {})) {
        checks.push({
          checkType: 'row_count',
          name: `${table} restored`,
          actual: String(count),
          // Zero rows is reported, not failed: an empty table can be legitimate.
          // What matters is that the number is visible rather than assumed.
          status: 'passed',
        });
      }
    } catch (err) {
      checks.push({
        checkType: 'connectivity',
        name: `Scratch database "${database}" accepts queries`,
        status: 'failed',
        actual: (err as Error).message.slice(0, 200),
      });
    } finally {
      await scratch.$disconnect().catch(() => undefined);
    }

    checks.push({
      checkType: 'schema_compatibility',
      name: 'Restored schema matches what the running code expects',
      expected: head ?? 'unknown',
      actual: restoredVersion ?? 'unknown',
      status: restoredVersion && head && restoredVersion === head ? 'passed' : 'failed',
    });

    return checks;
  }

  /**
   * Delete a backup object, keeping the run row as the record that it existed.
   *
   * ── THE THREE REFUSALS, AND WHY EACH ONE EARNS ITS PLACE ────────────────────
   *
   * 1. Physical backups. pgBackRest owns its repository layout and knows which
   *    WAL segments are still required by which retained backup. Deleting a base
   *    backup out from under it leaves WAL that replays onto nothing and a
   *    repository that reports healthy while being unrestorable. Those expire
   *    through `pgbackrest expire`, which understands the dependency graph.
   *
   * 2. The last surviving backup. Deleting it takes the system from "one copy"
   *    to "none" — the exact state this whole subsystem exists to prevent — and
   *    it is far more likely to be a misclick than an intention.
   *
   * 3. A run with nothing to delete. A row already deleted, or one that never
   *    produced an object (refused, failed), has no object to remove; saying so
   *    beats a success message for work that did not happen.
   */
  async deleteRun(runId: string, actorId: string): Promise<{ deleted: boolean; message: string }> {
    const run = await this.prisma.backupRun.findUnique({ where: { id: runId } });
    if (!run) throw new BadRequestException('Backup run not found');

    if (run.status === 'deleted') {
      return { deleted: false, message: 'This backup was already deleted.' };
    }

    if (run.backupType !== 'logical') {
      throw new BadRequestException(
        `"${run.backupType}" backups are managed by pgBackRest and cannot be deleted individually — ` +
          `it tracks which WAL each retained backup still needs, and removing one by hand leaves the ` +
          `repository unrestorable. Reduce retention instead, and let expire do it.`
      );
    }

    if (!run.manifestKey) {
      return {
        deleted: false,
        message: `This run (${run.status}) never produced an object, so there is nothing to delete.`,
      };
    }

    const survivors = await this.prisma.backupRun.count({
      where: {
        backupType: 'logical',
        status: 'completed',
        manifestKey: { not: null },
        id: { not: run.id },
      },
    });

    if (survivors === 0) {
      throw new BadRequestException(
        'Refusing to delete the last remaining backup. Take a new one first — deleting this ' +
          'leaves the platform with no restorable copy at all.'
      );
    }

    await this.local.deleteObject(run.manifestKey);

    await this.prisma.backupRun.update({
      where: { id: run.id },
      data: { status: 'deleted', deletedAt: new Date(), deletedBy: actorId },
    });

    const repository = await this.prisma.backupRepository.findFirst({
      where: { kind: 's3', isActive: true },
    });
    if (repository) await this.refreshLocalUsage(repository.id);

    this.logger.warn(`backup ${run.manifestKey} deleted by ${actorId}`);

    return { deleted: true, message: `Deleted ${run.manifestKey}. ${survivors} backup(s) remain.` };
  }

  /**
   * Recount local-disk usage from what is actually on disk.
   *
   * Derived rather than incremented, for the same reason the R2 counterpart is:
   * a running total that drifts is worse than no total, because it is trusted.
   */
  private async refreshLocalUsage(repositoryId: string): Promise<void> {
    const usage = await this.local.usage();
    // All three prefixes: dumps, base backups and WAL.
    const used = usage.dumpBytes + usage.baseBytes + usage.walBytes;

    const repo = await this.prisma.backupRepository.findUnique({ where: { id: repositoryId } });
    const pct = repo && repo.quotaBytes > 0n ? Number((used * 100n) / repo.quotaBytes) : 0;

    await this.prisma.backupRepository.update({
      where: { id: repositoryId },
      data: {
        usedBytes: used,
        backupBytes: usage.dumpBytes + usage.baseBytes,
        walBytes: usage.walBytes,
        objectCount: usage.objectCount,
        status:
          repo && repo.quotaBytes > 0n && pct >= repo.hardLimitPct
            ? 'over_quota'
            : repo && repo.quotaBytes > 0n && pct >= repo.softLimitPct
              ? 'degraded'
              : 'ok',
        lastCheckedAt: new Date(),
        lastError: null,
      },
    });
  }

  /**
   * Collect archived WAL on hosts using the local backend.
   *
   * Finds nothing until `archive_mode` is turned on and the server restarted
   * (ops/database/postgresql.windows.conf), which is the honest result — the
   * alternative would be a continuous band on the recovery timeline that no
   * data supports.
   */
  @Cron('*/2 * * * *')
  async collectLocalWal(): Promise<void> {
    if (this.backend() !== 'local') return;

    try {
      const target = await this.prisma.backupTarget.findFirst({ where: { isActive: true } });
      if (!target) return;

      // Say so when nothing is archiving.
      //
      // WalMonitorService — which owns the wal_lag and archive_backlog alerts —
      // returns immediately unless the pgBackRest agent is configured, so on a
      // local-backend host NOTHING watches WAL at all. Combined with collectWal
      // quietly returning 0, a server can run for weeks taking base backups it
      // can never replay past. That is the "successful backup, failed recovery"
      // gap this subsystem exists to close, so it gets an alert of its own.
      const blocked = this.local.walArchivingBlockedReason();
      if (blocked) {
        await this.alerts.raise({
          kind: 'wal_archiving_off',
          severity: 'critical',
          targetId: target.id,
          title: 'WAL archiving is not running — point-in-time recovery is not available',
          detail: { reason: blocked },
        });
        return;
      }

      await this.alerts.resolve('wal_archiving_off', target.id);

      const repository = await this.prisma.backupRepository.findFirst({
        where: { kind: 's3', isActive: true },
      });

      const { collected } = await this.local.collectWal(target.id, repository?.id ?? null);
      if (collected) {
        this.logger.log(`uploaded ${collected} WAL segment(s) to R2`);
        if (repository) await this.refreshLocalUsage(repository.id);
      }

      // Nothing is kept on local disk. The archive directory is a handoff
      // point for archive_command, not a store — segments confirmed present in
      // R2 at the right size are removed, so the directory cannot grow without
      // bound and fill the disk the database lives on.
      await this.local.pruneUploadedWal(target.id);
    } catch (err) {
      this.logger.warn(`local WAL collection skipped: ${(err as Error).message}`);
    }
  }

  private async schemaHead(): Promise<string | null> {
    const head = await this.prisma.schemaMigration.findFirst({
      where: { database: 'shopora' },
      orderBy: { sequence: 'desc' },
      select: { filename: true },
    });
    return head?.filename ?? null;
  }

  /**
   * SLA check: has each policy produced a successful run recently enough?
   *
   * Separate from the failure alert because the dangerous case is a policy that
   * silently stops running at all — no failures, because no attempts. Nothing
   * else in the system would notice.
   */
  @Cron('7 * * * *')
  async checkSla(): Promise<void> {
    // Nowhere to write means no backups are expected, so an SLA breach is not
    // meaningful — this would otherwise page every developer hourly.
    if (this.backend() === 'none') return;

    let policies: Array<{ id: string; name: string; targetId: string; slaHours: number }>;
    try {
      policies = await this.prisma.backupPolicy.findMany({
        where: { isEnabled: true, isActive: true, deletedAt: null },
        select: { id: true, name: true, targetId: true, slaHours: true },
      });
    } catch (err) {
      this.logger.warn(`SLA check skipped: ${(err as Error).message}`);
      return;
    }

    for (const policy of policies) {
      // Per-policy, because the loop must survive one policy's failure. A
      // duplicate-key error on the first policy previously aborted the sweep
      // before the other five were evaluated, so a single bad alert row
      // silenced SLA monitoring for every policy behind it.
      try {
        const cutoff = new Date(Date.now() - policy.slaHours * 60 * 60 * 1000);
        const recent = await this.prisma.backupRun.findFirst({
          where: { policyId: policy.id, status: 'completed', finishedAt: { gte: cutoff } },
        });

        if (recent) {
          await this.alerts.resolve('sla_breach', policy.targetId);
          continue;
        }

        await this.alerts.raise({
          kind: 'sla_breach',
          severity: 'critical',
          targetId: policy.targetId,
          title: `No successful "${policy.name}" backup in ${policy.slaHours}h`,
          detail: { policyId: policy.id, slaHours: policy.slaHours },
        });
      } catch (err) {
        this.logger.error(`SLA check failed for "${policy.name}": ${(err as Error).message}`);
      }
    }
  }

  /**
   * Next fire time for a 5-field cron expression.
   *
   * Minute-resolution scan over the next 366 days. Deliberately dependency-free:
   * this module already introduces a privileged daemon, and adding a cron
   * parser to the API's dependency tree for one function is a poor trade. The
   * scan is bounded and runs once per policy per fire.
   */
  nextRun(expression: string, from: Date = new Date()): Date | null {
    const parts = expression.trim().split(/\s+/);
    if (parts.length !== 5) return null;

    const [min, hour, dom, month, dow] = parts;
    const cursor = new Date(from.getTime());
    cursor.setSeconds(0, 0);
    cursor.setMinutes(cursor.getMinutes() + 1);

    const limit = 366 * 24 * 60;
    for (let i = 0; i < limit; i++) {
      if (
        this.fieldMatches(min, cursor.getMinutes()) &&
        this.fieldMatches(hour, cursor.getHours()) &&
        this.fieldMatches(dom, cursor.getDate()) &&
        this.fieldMatches(month, cursor.getMonth() + 1) &&
        this.fieldMatches(dow, cursor.getDay())
      ) {
        return new Date(cursor.getTime());
      }
      cursor.setMinutes(cursor.getMinutes() + 1);
    }
    return null;
  }

  /** Supports `*`, `a`, `a,b`, `a-b`, wildcard-with-step, and `a-b/n`. */
  private fieldMatches(field: string, value: number): boolean {
    if (field === '*') return true;

    for (const part of field.split(',')) {
      const [range, stepRaw] = part.split('/');
      const step = stepRaw ? Number(stepRaw) : 1;
      if (!Number.isFinite(step) || step < 1) continue;

      if (range === '*') {
        if (value % step === 0) return true;
        continue;
      }

      const [startRaw, endRaw] = range.split('-');
      const start = Number(startRaw);
      const end = endRaw === undefined ? start : Number(endRaw);
      if (!Number.isFinite(start) || !Number.isFinite(end)) continue;

      if (value >= start && value <= end && (value - start) % step === 0) return true;
    }

    return false;
  }

  /**
   * Are we still close enough to the scheduled time to be worth starting?
   *
   * Guards the case where the API was down over the scheduled slot and comes
   * back mid-morning: firing a full backup then is worse than skipping it,
   * because the next one is only a day away and this one would land on top of
   * live traffic.
   */
  private insideWindow(expression: string, windowMinutes: number): boolean {
    const previous = this.previousRun(expression);
    if (!previous) return true;
    return Date.now() - previous.getTime() <= windowMinutes * 60 * 1000;
  }

  /**
   * The most recent time this expression fired at or before `from`.
   *
   * ── WHY THE LOOKBACK EXPANDS ────────────────────────────────────────────────
   *
   * This used to look back exactly 24 hours. For any cron that fires less often
   * than daily that finds nothing, and `insideWindow` reads "no previous fire"
   * as "inside the window" — so the weekly full backup's 4-hour window was
   * enforced on Sundays and ignored the other six days. A guard that is off
   * most of the time is worse than no guard, because the screen still shows a
   * window.
   *
   * The lookback therefore widens until it finds one: a day, a week, a month, a
   * year. It stops at the first hit, so the common daily case still costs one
   * pass, and only genuinely sparse schedules pay for the wider scans.
   */
  previousRun(expression: string, from: Date = new Date()): Date | null {
    for (const days of [1, 8, 32, 370]) {
      const start = new Date(from.getTime() - days * 24 * 60 * 60 * 1000);
      let cursor = this.nextRun(expression, start);
      let last: Date | null = null;

      while (cursor && cursor <= from) {
        last = cursor;
        cursor = this.nextRun(expression, cursor);
      }

      if (last) return last;
    }

    return null;
  }
}
